import { ReactNode, useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import {
    ActionIcon,
    Alert,
    Box,
    Button,
    Card,
    Flex,
    Grid,
    Group,
    Image,
    LoadingOverlay,
    Stack,
    Text,
    TextInput,
    Tooltip,
} from "@mantine/core";
import { IconInfoCircle, IconPlus, IconTrash } from "@tabler/icons-react";
import { pick } from "lodash";
import { Product, ReviewDestinationInput } from "src/graphql/generated/graphql";
import { useUploadImage } from "src/hooks/files/useUploadImage";
import { handleError } from "src/utils/error";

import { productImageCrop, useQueryProductByEAN } from "../../../hooks/products";
import { SelectImageModal } from "../../../modals/SelectImage";
import { FormItem } from "../../FormItem";
import { ReviewDestinationsEditor } from "../../ReviewDestinationsEditor";
import { RichTextEditor } from "../../RichTextEditor";
import { SelectProductCategory } from "../../SelectProductCategory";
import { EanCheckRef, useCreateCampaignInquiryFormContext } from "../formContext";

const EAN_PATTERN = /^[0-9]{13}$/;

export interface ItemProps {
    index: number;
    reviewDestinations: ReviewDestinationInput[];
    noReviewDestinationsLabel?: ReactNode;
    enableCustomProductReviewDestinations?: boolean;
    eanCheckRef?: EanCheckRef;
    onRemove?();
}

export const Item = ({
    index,
    reviewDestinations,
    noReviewDestinationsLabel,
    enableCustomProductReviewDestinations,
    eanCheckRef,
    onRemove,
}: ItemProps) => {
    const { t } = useTranslation(["common"]);

    const createCampaignInquiryForm = useCreateCampaignInquiryFormContext();

    const isExistingProduct = !!createCampaignInquiryForm.values?.products?.[index]?.id;

    const [isCheckingEAN, setIsCheckingEAN] = useState(false);
    // The catalogue product that already owns the EAN the client just typed. Set
    // only when we did NOT link — see handleEANEdited.
    const [conflictProduct, setConflictProduct] = useState<Product | null>(null);
    const { queryProductByEAN } = useQueryProductByEAN();

    /**
     * Adopt a catalogue product. MERGES — the client's destinations, description,
     * images, attributes, links and info are theirs and must survive being linked
     * (TTV2-1569: this used to replace the whole product with five fields).
     */
    const handleLinkProduct = useCallback(
        (product: Product) => {
            const current = createCampaignInquiryForm.values?.products?.[index] || {};

            createCampaignInquiryForm.setFieldValue(`products.${index}`, {
                ...current,
                id: product?.id,
                ean: product?.codes?.ean ?? current.ean,
                name: product?.name ?? current.name,
                category: product?.category ?? current.category,
            });

            setConflictProduct(null);
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [index]
    );

    /**
     * Break the link to the catalogue so the fields become editable again. Clears
     * the EAN as well: it belongs to the product being rejected, and leaving it
     * would greet the client with a conflict error about that very product.
     * Everything else they authored is kept.
     */
    const handleUnlinkProduct = useCallback(() => {
        const current = createCampaignInquiryForm.values?.products?.[index] || {};

        createCampaignInquiryForm.setFieldValue(`products.${index}`, {
            ...current,
            id: null,
            ean: null,
        });

        setConflictProduct(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [index]);

    /**
     * Runs only from the EAN field's onChange — never on mount or when a draft or
     * a copied inquiry is rehydrated. That is deliberate: an effect watching the
     * value cannot tell "the client typed this" from "state was restored", and
     * treating the latter as the former is what silently relinked and overwrote
     * copied products (TTV2-1569).
     */
    const handleEANEdited = useCallback(
        async (value: string) => {
            setConflictProduct(null);

            const ean = (value || "").trim();
            if (!EAN_PATTERN.test(ean)) {
                return;
            }

            if (eanCheckRef) {
                eanCheckRef.current.pending[ean] = true;
            }
            setIsCheckingEAN(true);

            try {
                const product = await queryProductByEAN(ean, createCampaignInquiryForm.values?.products?.[index]?.id);

                if (product?.id) {
                    // We already stock this EAN. Do not adopt the product on the
                    // client's behalf — record the clash, show it, and let them
                    // choose. They may link to it or enter a different EAN, but
                    // they may not ask us to create a second product under it.
                    if (eanCheckRef) {
                        eanCheckRef.current.conflicts[ean] = {
                            productId: product.id,
                            name: product.name || "",
                            ean,
                        };
                    }
                    setConflictProduct(product);
                }
            } finally {
                setIsCheckingEAN(false);
                if (eanCheckRef) {
                    delete eanCheckRef.current.pending[ean];
                }
                createCampaignInquiryForm.validateField(`products.${index}.ean`);
            }
        },
        // the form object is not memoized in this version of mantine form, so
        // depending on it would re-create this callback on every render
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [index, eanCheckRef, queryProductByEAN]
    );

    const eanInputProps = createCampaignInquiryForm.getInputProps(`products.${index}.ean`);

    const { uploadImage, isUploadingImage } = useUploadImage();

    const handleImageUpload = useCallback(
        async (file: File) => {
            try {
                const image = await uploadImage(file);
                if (!image?.id) {
                    return image;
                }

                createCampaignInquiryForm.setFieldValue(`products.${index}.images`, [
                    ...(createCampaignInquiryForm.values?.products?.[index]?.images || []),
                    pick(image, ["id", "objectKey", "src"]),
                ]);

                return image;
            } catch (err) {
                handleError(err, { context: { productId: createCampaignInquiryForm.values?.products?.[index]?.id } });
            }
            // eslint-disable-next-line react-hooks/exhaustive-deps
        },
        [JSON.stringify(createCampaignInquiryForm.values?.products?.[index]?.images)]
    );

    const destinations = createCampaignInquiryForm.values?.products?.[index]?.destinations?.map((dest) => {
        return {
            // Spread first: enumerating the fields here silently dropped isOrderNumber, so
            // Datart campaigns never asked testers for an order number (TTV2-1623). `config`
            // stays `?? undefined` — the tenant catalog omits it, and "" is not "unset".
            ...dest,
            key: dest.key || "",
            name: dest.name || "",
            type: dest.type || "text",
            config: dest.config ?? undefined,
            configFields: dest.configFields?.map((field) => ({
                label: field.label || "",
                path: field.path || "",
                type: field.type || "text",
                placeholder: field.placeholder || "",
                isRequiredForApproval: field.isRequiredForApproval || false,
                urlStripQueryString: field.urlStripQueryString || false,
            })),
        };
    });

    return (
        <Card p={36} radius={12}>
            <Card.Section p="md">
                <LoadingOverlay visible={isUploadingImage} />
                <Flex align="stretch">
                    <Grid style={{ flex: 1 }}>
                        <Grid.Col xs={12} md={6}>
                            <TextInput
                                {...eanInputProps}
                                label={t("product_code_ean")}
                                placeholder={t("product_code_ean_placeholder")}
                                disabled={isExistingProduct}
                                description={isCheckingEAN ? t("product_code_ean_checking") : undefined}
                                onChange={(event) => {
                                    eanInputProps.onChange(event);
                                    handleEANEdited(event.currentTarget.value);
                                }}
                            />
                        </Grid.Col>
                        <Grid.Col xs={12} md={6}>
                            <SelectProductCategory
                                {...createCampaignInquiryForm.getInputProps(`products.${index}.category`)}
                                disabled={isExistingProduct}
                                withAsterisk
                            />
                        </Grid.Col>
                        <Grid.Col xs={12}>
                            <TextInput
                                {...createCampaignInquiryForm.getInputProps(`products.${index}.name`)}
                                label={t("product_name")}
                                placeholder={t("product_name")}
                                disabled={isExistingProduct}
                                withAsterisk
                            />
                        </Grid.Col>
                        {isExistingProduct && (
                            <Grid.Col xs={12}>
                                <Group spacing="xs">
                                    <Text size="sm" color="dimmed">
                                        {t("campaigninquiry_transform_product_alreadyexists")}
                                    </Text>
                                    <Button variant="subtle" size="sm" onClick={handleUnlinkProduct}>
                                        {t("campaigninquiry_transform_product_selectother")}
                                    </Button>
                                </Group>
                            </Grid.Col>
                        )}
                        {!isExistingProduct && !!conflictProduct && (
                            <Grid.Col xs={12}>
                                <Alert color="orange" title={t("product_code_ean_alreadyexists")}>
                                    <Stack align="flex-start" spacing="xs">
                                        <Text size="sm">
                                            {t("product_code_ean_conflict", {
                                                ean: conflictProduct.codes?.ean,
                                                name: conflictProduct.name,
                                            })}
                                        </Text>
                                        <Button
                                            variant="light"
                                            size="sm"
                                            onClick={() => handleLinkProduct(conflictProduct)}
                                        >
                                            {t("product_code_ean_conflict_use")}
                                        </Button>
                                    </Stack>
                                </Alert>
                            </Grid.Col>
                        )}
                        {!isExistingProduct && (
                            <>
                                <Grid.Col xs={12} md={6}>
                                    <TextInput
                                        {...createCampaignInquiryForm.getInputProps(
                                            `products.${index}.info.manufacturer`
                                        )}
                                        label={t("product_manufacturer")}
                                        placeholder={t("product_manufacturer")}
                                    />
                                </Grid.Col>
                                <Grid.Col xs={12} md={6}>
                                    <TextInput
                                        {...createCampaignInquiryForm.getInputProps(`products.${index}.info.model`)}
                                        label={t("product_model")}
                                        placeholder={t("product_model")}
                                    />
                                </Grid.Col>
                                <Grid.Col xs={12}>
                                    <FormItem
                                        mb="xs"
                                        label={
                                            <Tooltip w="250px" label={t("product_description_info")} multiline>
                                                <Text w="fit-content">
                                                    {t("product_description")} <IconInfoCircle size={12} />
                                                </Text>
                                            </Tooltip>
                                        }
                                    >
                                        <RichTextEditor
                                            placeholder={t("product_description")}
                                            value={createCampaignInquiryForm.values?.products?.[index]?.description}
                                            onChange={(description) =>
                                                createCampaignInquiryForm.setFieldValue(
                                                    `products.${index}.description`,
                                                    description
                                                )
                                            }
                                            onImageUpload={handleImageUpload}
                                        />
                                    </FormItem>
                                </Grid.Col>
                                <Grid.Col xs={12}>
                                    <FormItem mb="xs" label={t("product_thumbnailimage")}>
                                        <Flex>
                                            <Stack spacing="sm">
                                                <Button
                                                    variant="light"
                                                    onClick={() =>
                                                        SelectImageModal.display({
                                                            title: t("thumbnailimage_upload"),
                                                            openFileInputDialogOnMount: true,
                                                            crop: productImageCrop,
                                                            onSelect: (thumbnailImage) => {
                                                                createCampaignInquiryForm.setFieldValue(
                                                                    `products.${index}.thumbnailImage`,
                                                                    pick(thumbnailImage, ["id", "objectKey", "src"])
                                                                );
                                                            },
                                                        })
                                                    }
                                                >
                                                    {t("thumbnailimage_upload")}
                                                </Button>
                                                {!!createCampaignInquiryForm.values?.products?.[index]?.thumbnailImage
                                                    ?.src && (
                                                    <Button
                                                        variant="subtle"
                                                        color="red"
                                                        onClick={() =>
                                                            createCampaignInquiryForm.setFieldValue(
                                                                `products.${index}.thumbnailImage`,
                                                                null
                                                            )
                                                        }
                                                    >
                                                        {t("image_remove")}
                                                    </Button>
                                                )}
                                            </Stack>
                                            <Box ml="md" style={{ flex: 1 }}>
                                                {!!createCampaignInquiryForm.values?.products?.[index]?.thumbnailImage
                                                    ?.src && (
                                                    <Image
                                                        radius="md"
                                                        src={`${createCampaignInquiryForm.values.products[index].thumbnailImage.src}?w=320`}
                                                        width="320px"
                                                    />
                                                )}
                                            </Box>
                                        </Flex>
                                    </FormItem>
                                </Grid.Col>
                                <Grid.Col xs={12}>
                                    <TextInput
                                        {...createCampaignInquiryForm.getInputProps(`products.${index}.userManualLink`)}
                                        mb="xs"
                                        type="text"
                                        label={t("product_usermanuallink")}
                                        placeholder={t("product_usermanuallink_placeholder")}
                                    />
                                </Grid.Col>
                                <Grid.Col xs={12}>
                                    <FormItem mb="xs" label={t("product_detailpagelinks")}>
                                        <Stack spacing="0">
                                            {(
                                                createCampaignInquiryForm.values?.products?.[index]?.detailPageLinks ||
                                                []
                                            ).map((_, index) => (
                                                <Flex key={`${index}`} align="center" mb="md">
                                                    <ActionIcon
                                                        color="red"
                                                        onClick={() =>
                                                            createCampaignInquiryForm.removeListItem(
                                                                `products.${index}.detailPageLinks`,
                                                                index
                                                            )
                                                        }
                                                    >
                                                        <IconTrash size="1em" />
                                                    </ActionIcon>
                                                    <TextInput
                                                        {...createCampaignInquiryForm.getInputProps(
                                                            `products.${index}.detailPageLinks.${index}.label`
                                                        )}
                                                        ml="md"
                                                        label={t("product_detailpagelink_label")}
                                                        placeholder={t("product_detailpagelink_label_placeholder")}
                                                        style={{ flex: 3 }}
                                                    />
                                                    <TextInput
                                                        {...createCampaignInquiryForm.getInputProps(
                                                            `products.${index}.detailPageLinks.${index}.src`
                                                        )}
                                                        ml="md"
                                                        label={t("product_detailpagelink_src")}
                                                        placeholder={t("product_detailpagelink_src_placeholder")}
                                                        style={{ flex: 5 }}
                                                    />
                                                </Flex>
                                            ))}
                                            <Button
                                                variant="subtle"
                                                size="sm"
                                                leftIcon={<IconPlus size="1em" />}
                                                onClick={() =>
                                                    createCampaignInquiryForm.insertListItem(
                                                        `products.${index}.detailPageLinks`,
                                                        {
                                                            label: "",
                                                            src: "",
                                                        }
                                                    )
                                                }
                                            >
                                                {t("product_detailpagelink_add")}
                                            </Button>
                                        </Stack>
                                    </FormItem>
                                </Grid.Col>
                            </>
                        )}
                    </Grid>
                    <Box m="md">
                        {typeof onRemove === "function" && (
                            <Tooltip label={t("product_remove")}>
                                <ActionIcon color="red" onClick={onRemove}>
                                    <IconTrash />
                                </ActionIcon>
                            </Tooltip>
                        )}
                    </Box>
                </Flex>
            </Card.Section>
            <Card.Section py="md" inheritPadding withBorder>
                <FormItem label={t("campaign_productreviewdestinations")}>
                    <ReviewDestinationsEditor
                        value={destinations}
                        reviewDestinations={reviewDestinations}
                        noReviewDestinationsLabel={noReviewDestinationsLabel}
                        enableCustomProductReviewDestinations={enableCustomProductReviewDestinations}
                        onChange={(destinations) =>
                            createCampaignInquiryForm.setFieldValue(`products.${index}.destinations`, destinations)
                        }
                    />
                </FormItem>
            </Card.Section>
        </Card>
    );
};
